import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { useTheme } from '../../contexts/ThemeContext'
import { IcSeed, IcWind, IcLeaf, IcChart, IcTarget, IcWallet } from '../ui/Icons'
import { usePresupuestoContext } from '../../contexts/PresupuestoContext'

const C = 314.16

const getEmociones = (t) => [
  { label: 'Necesario', color: t.sage   },
  { label: 'Me alegró', color: t.gold   },
  { label: 'Impulsivo', color: t.blush  },
  { label: 'Obligación', color: t.mauve },
]

const fmt = (n) => Math.round(n).toLocaleString('es')

export default function Resumen() {
  const { theme } = useTheme()
  const s = makeStyles(theme)
  const EMOCIONES = getEmociones(theme)

  const { limites, loadMes } = usePresupuestoContext()

  const [mesOffset, setMesOffset] = useState(0)
  const [gastos, setGastos] = useState([])
  const [loading, setLoading] = useState(true)

  const ahora = new Date()
  const mesRef = new Date(ahora.getFullYear(), ahora.getMonth() + mesOffset, 1)

  // Cargar presupuesto del mes al navegar
  useEffect(() => {
    loadMes(mesRef.getFullYear(), mesRef.getMonth() + 1)
  }, [mesOffset])
  const mesLabel = mesRef.toLocaleDateString('es', { month: 'long', year: 'numeric' })
  const diasMes = new Date(mesRef.getFullYear(), mesRef.getMonth() + 1, 0).getDate()
  const primerDia = mesRef.getDay()
  const offsetLun = primerDia === 0 ? 6 : primerDia - 1

  useEffect(() => {
    async function fetchMes() {
      setLoading(true)
      const now = new Date()
      const inicio = new Date(now.getFullYear(), now.getMonth() + mesOffset, 1)
      const fin = new Date(now.getFullYear(), now.getMonth() + mesOffset + 1, 0, 23, 59, 59)

      const { data } = await supabase
        .from('gastos')
        .select('*')
        .gte('fecha', inicio.toISOString())
        .lte('fecha', fin.toISOString())
        .order('fecha', { ascending: false })

      setGastos(data || [])
      setLoading(false)
    }
    fetchMes()
  }, [mesOffset])

  const totales = gastos.reduce((acc, g) => {
    if (g.tipo === 'gasto')   acc.gastos   += g.monto
    if (g.tipo === 'ingreso') acc.ingresos += g.monto
    if (g.tipo === 'ahorro')  acc.ahorros  += g.monto
    return acc
  }, { gastos: 0, ingresos: 0, ahorros: 0 })

  const soloGastos = useMemo(() => gastos.filter(g => g.tipo === 'gasto'), [gastos])

  const porEmocion = useMemo(() =>
    soloGastos.reduce((acc, g) => ({ ...acc, [g.emocion]: (acc[g.emocion] || 0) + g.monto }), {}),
  [soloGastos])

  const totalEmo = Object.values(porEmocion).reduce((a, b) => a + b, 0)

  const segmentos = useMemo(() => {
    let acum = 0
    return EMOCIONES.map(e => {
      const monto = porEmocion[e.label] || 0
      const pct   = totalEmo > 0 ? monto / totalEmo : 0
      const dash  = pct * C
      const rot   = acum * 360 - 90
      acum += pct
      return { ...e, monto, pct, dash, gap: C - dash, rot }
    }).filter(s => s.dash > 0)
  }, [porEmocion, totalEmo])

  const porCategoria = useMemo(() =>
    soloGastos.reduce((acc, g) => ({ ...acc, [g.categoria]: (acc[g.categoria] || 0) + g.monto }), {}),
  [soloGastos])

  const porDia = useMemo(() =>
    soloGastos.reduce((acc, g) => {
      const d = new Date(g.fecha).getDate()
      return { ...acc, [d]: (acc[d] || 0) + g.monto }
    }, {}),
  [soloGastos])

  const maxDia = Math.max(...Object.values(porDia), 1)

  const limitesGasto = useMemo(
    () => Object.fromEntries(Object.entries(limites).filter(([cat]) => !cat.startsWith('__'))),
    [limites]
  )
  const totalPresupuesto = useMemo(
    () => Object.values(limitesGasto).reduce((a, b) => a + b, 0),
    [limitesGasto]
  )
  const catsOverBudget = useMemo(
    () => Object.entries(limitesGasto).filter(([cat, lim]) => lim > 0 && (porCategoria[cat] || 0) > lim),
    [limitesGasto, porCategoria]
  )
  const catsNearLimit = useMemo(
    () => Object.entries(limitesGasto).filter(([cat, lim]) => {
      const g = porCategoria[cat] || 0
      return lim > 0 && g <= lim && g / lim >= 0.8
    }),
    [limitesGasto, porCategoria]
  )

  const heatColor = (ratio) => [
    theme.cream,
    theme.blush + '55',
    theme.blush + 'AA',
    theme.terra + '88',
    theme.terra,
  ][Math.floor(ratio * 4)]

  const insights = useMemo(() => {
    const result = []
    const semanaAtras = Date.now() - 7 * 86400000
    const aportesSem = gastos.filter(g => g.tipo === 'ahorro' && new Date(g.fecha) >= semanaAtras)
    if (aportesSem.length > 0) result.push({
      type: 'positive', Icon: IcSeed,
      title: 'aportes esta semana',
      text: `sumaste $${aportesSem.reduce((a, g) => a + g.monto, 0).toLocaleString('es')} a tu meta.`
    })
    const impulsivos = gastos.filter(g => g.tipo === 'gasto' && g.emocion === 'Impulsivo')
    if (impulsivos.length >= 2) result.push({
      type: 'warn', Icon: IcWind,
      title: `${impulsivos.length} gastos impulsivos`,
      text: '¿algo los provocó, o fue el momento?'
    })
    // Budget insights
    if (catsOverBudget.length > 0) result.push({
      type: 'warn', Icon: IcWallet,
      title: `${catsOverBudget.length === 1 ? 'una categoría' : `${catsOverBudget.length} categorías`} sobre el límite`,
      text: `${catsOverBudget.map(([c]) => c).join(', ')} pasaron el límite. ¿fue algo esperado?`
    })
    if (catsOverBudget.length === 0 && catsNearLimit.length > 0) result.push({
      type: 'warn', Icon: IcWallet,
      title: `${catsNearLimit.map(([c]) => c).join(', ')} al límite`,
      text: `poco margen en ${catsNearLimit.map(([c]) => c).join(', ')} para lo que queda del mes.`
    })
    if (catsOverBudget.length === 0 && catsNearLimit.length === 0 && totalPresupuesto > 0 && totales.gastos > 0 && totales.gastos <= totalPresupuesto) result.push({
      type: 'positive', Icon: IcWallet,
      title: 'dentro del presupuesto',
      text: `$${fmt(totales.gastos)} de $${fmt(totalPresupuesto)} gastados. dentro del presupuesto.`
    })
    return result
  }, [gastos, catsOverBudget, catsNearLimit, totalPresupuesto, totales.gastos])

  if (loading) return <div style={s.loading}>cargando</div>

  return (
    <div style={s.container}>

      {/* Header */}
      <div style={s.header}>
        <p style={s.eyebrow}>mirando hacia atrás</p>
        <div style={s.mesNav}>
          <button style={s.navBtn} onClick={() => setMesOffset(o => o - 1)}>‹</button>
          <h1 style={s.titulo}>{mesLabel}</h1>
          <button
            style={{ ...s.navBtn, opacity: mesOffset >= 0 ? 0.2 : 1 }}
            disabled={mesOffset >= 0}
            onClick={() => setMesOffset(o => o + 1)}
          >›</button>
        </div>
      </div>

      {/* Números */}
      <div style={s.numerosRow}>
        {[
          { Icon: IcLeaf,   val: totales.ingresos, label: 'Ingresos', color: theme.sage  },
          { Icon: IcChart,  val: totales.gastos,   label: 'Gastos',   color: theme.terra },
          { Icon: IcTarget, val: totales.ahorros,  label: 'Ahorrado', color: theme.gold  },
        ].map(({ Icon, val, label, color }, i) => (
          <div key={i} style={s.numCard}>
            <div style={{ ...s.numIcon, color }}><Icon size={20} /></div>
            <div style={{ ...s.numVal, color }}>${val.toLocaleString('es')}</div>
            <div style={s.numLabel}>{label}</div>
          </div>
        ))}
      </div>

      <>
          {/* Insights */}
          {insights.length > 0 && (
            <div style={s.insightsList}>
              {insights.map((ins, i) => (
                <div key={i} style={{ ...s.insightCard, ...(ins.type === 'warn' ? s.insightWarn : {}) }}>
                  <div style={{ ...s.insightIc, ...(ins.type === 'warn' ? s.insightIcWarn : {}) }}>
                    <ins.Icon size={18} />
                  </div>
                  <div>
                    <h4 style={s.insightH4}>{ins.title}</h4>
                    <p style={s.insightP}>{ins.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Dona emocional + leyenda */}
          {segmentos.length > 0 && (
            <div style={s.seccion}>
              <h2 style={s.secTitulo}>¿Cómo gasté? <span style={s.secHint}>por emoción</span></h2>
              <div style={s.donutCard}>
                <div style={s.donutWrap}>
                  <svg width="130" height="130" viewBox="0 0 120 120" style={{ display: 'block' }}>
                    <circle cx="60" cy="60" r="50" fill="none" stroke={theme.blush + '44'} strokeWidth="16" />
                    {segmentos.map((seg, i) => (
                      <circle key={i} cx="60" cy="60" r="50" fill="none"
                        stroke={seg.color} strokeWidth="16"
                        strokeDasharray={`${seg.dash} ${seg.gap}`}
                        transform={`rotate(${seg.rot} 60 60)`}
                      />
                    ))}
                  </svg>
                  <div style={s.donutCenter}>
                    <IcChart size={20} color={theme.inkLight} />
                    <span style={s.donutCenterLabel}>{mesLabel.split(' ')[0]}</span>
                  </div>
                </div>
                <div style={s.donutSide}>
                  <p style={s.donutEye}>gastos por emoción</p>
                  <p style={s.donutBig}>${totales.gastos.toLocaleString('es')}</p>
                  <p style={s.donutSm}>total gastado<br />{mesLabel}</p>
                </div>
              </div>
              <div style={s.legend}>
                {segmentos.map((seg, i) => (
                  <div key={i} style={s.legItem}>
                    <div style={{ ...s.legDot, background: seg.color }} />
                    <span>{seg.label}</span>
                    <span style={s.legVal}>{Math.round(seg.pct * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Barras por categoría */}
          {Object.keys(porCategoria).length > 0 && (
            <div style={s.seccion}>
              <h2 style={s.secTitulo}>Dónde fue mi dinero <span style={s.secHint}>por categoría</span></h2>
              {Object.entries(porCategoria).sort(([, a], [, b]) => b - a).map(([cat, monto]) => (
                <div key={cat} style={s.barRow}>
                  <div style={s.barTop}>
                    <span style={s.barNombre}>{cat}</span>
                    <span style={s.barMonto}>${monto.toLocaleString('es')}</span>
                  </div>
                  <div style={s.barTrack}>
                    <div style={{ ...s.barFill, width: `${Math.min((monto / totales.gastos) * 100, 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Presupuesto vs real */}
          {totalPresupuesto > 0 && (
            <div style={s.seccion}>
              <h2 style={s.secTitulo}>Presupuesto <span style={s.secHint}>real vs límite</span></h2>
              <div style={s.budgetCard}>
                <div style={s.budgetRow}>
                  <span style={s.budgetLabel}>gastado</span>
                  <span style={s.budgetVal}>${fmt(totales.gastos)}</span>
                  <span style={s.budgetSep}>/</span>
                  <span style={{ ...s.budgetTotal, color: totales.gastos > totalPresupuesto ? theme.terra : theme.inkMid }}>
                    ${fmt(totalPresupuesto)}
                  </span>
                </div>
                <div style={s.budgetTrack}>
                  <div style={{
                    ...s.budgetFill,
                    width: `${Math.min(100, Math.round((totales.gastos / totalPresupuesto) * 100))}%`,
                    background: totales.gastos > totalPresupuesto
                      ? `linear-gradient(90deg, ${theme.terraDeep}, ${theme.terra})`
                      : theme.cardGradient,
                  }} />
                </div>
                <div style={s.budgetFooter}>
                  <span style={{ color: totales.gastos > totalPresupuesto ? theme.terra : theme.inkLight }}>
                    {Math.min(100, Math.round((totales.gastos / totalPresupuesto) * 100))}% usado
                  </span>
                  <span style={{ color: totales.gastos > totalPresupuesto ? theme.terra : theme.sage }}>
                    {totales.gastos > totalPresupuesto
                      ? `+$${fmt(totales.gastos - totalPresupuesto)} sobre`
                      : `$${fmt(totalPresupuesto - totales.gastos)} libre`}
                  </span>
                </div>
              </div>
              {Object.entries(limitesGasto)
                .filter(([cat]) => (porCategoria[cat] || 0) > 0 || limitesGasto[cat] > 0)
                .sort(([catA], [catB]) => {
                  const pctA = (porCategoria[catA] || 0) / limitesGasto[catA]
                  const pctB = (porCategoria[catB] || 0) / limitesGasto[catB]
                  return pctB - pctA
                })
                .map(([cat, lim]) => {
                  const gastado = porCategoria[cat] || 0
                  const pct = Math.min(100, Math.round((gastado / lim) * 100))
                  const over = gastado > lim
                  return (
                    <div key={cat} style={s.budgetCatRow}>
                      <div style={s.budgetCatTop}>
                        <span style={s.budgetCatNombre}>{cat}</span>
                        <span style={{ ...s.budgetCatMonto, color: over ? theme.terra : theme.ink }}>
                          ${fmt(gastado)} / ${fmt(lim)}
                        </span>
                      </div>
                      <div style={s.budgetCatTrack}>
                        <div style={{
                          ...s.budgetCatFill,
                          width: `${pct}%`,
                          background: over
                            ? `linear-gradient(90deg, ${theme.terraDeep}, ${theme.terra})`
                            : `linear-gradient(90deg, ${theme.blush}, ${theme.terra})`,
                        }} />
                      </div>
                    </div>
                  )
                })}
            </div>
          )}

          {/* Heatmap */}
          <div style={s.seccion}>
            <h2 style={s.secTitulo}>Días de gasto <span style={s.secHint}>mapa del mes</span></h2>
            <div style={s.heatCard}>
              <div style={s.heatGrid}>
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => (
                  <div key={d} style={s.heatLabel}>{d}</div>
                ))}
                {Array.from({ length: offsetLun }).map((_, i) => <div key={`e${i}`} />)}
                {Array.from({ length: diasMes }).map((_, i) => {
                  const dia   = i + 1
                  const monto = porDia[dia] || 0
                  const ratio = monto / maxDia
                  const bg    = monto === 0 ? theme.cream : heatColor(Math.max(ratio, 0.25))
                  return <div key={dia} style={{ ...s.heatCell, background: bg }}
                    title={monto > 0 ? `${dia} · $${monto.toLocaleString('es')}` : undefined} />
                })}
              </div>
              <div style={s.heatLegend}>
                <span style={s.heatLegendLabel}>menos</span>
                {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                  <div key={i} style={{ width: 12, height: 12, borderRadius: 3, background: ratio === 0 ? theme.cream : heatColor(ratio) }} />
                ))}
                <span style={s.heatLegendLabel}>más</span>
              </div>
            </div>
          </div>

      </>

      {gastos.length === 0 && (
        <div style={s.vacio}>
          <p style={s.vacioTexto}>sin registros en {mesLabel}</p>
          <p style={s.vacioSub}>navega a otro mes o empieza a registrar</p>
        </div>
      )}

    </div>
  )
}

const makeStyles = (t) => ({
  container:       { maxWidth: 560, margin: '0 auto', padding: '32px 28px 110px', fontFamily: "'DM Sans', sans-serif", background: t.cream, minHeight: '100vh' },
  loading:         { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: t.inkLight },
  header:          { marginBottom: 24 },
  eyebrow:         { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 14, color: t.inkLight, marginBottom: 4 },
  mesNav:          { display: 'flex', alignItems: 'center', gap: 12 },
  titulo:          { fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300, color: t.ink, letterSpacing: -0.5, flex: 1 },
  navBtn:          { background: 'none', border: 'none', fontSize: 26, color: t.inkMid, cursor: 'pointer', padding: '4px 8px', fontFamily: 'inherit', lineHeight: 1 },
  numerosRow:      { display: 'flex', gap: 10, marginBottom: 16 },
  numCard:         { flex: 1, background: t.paper, border: `1px solid ${t.blush}44`, borderRadius: 20, padding: '16px 10px', textAlign: 'center' },
  numIcon:         { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6, height: 24 },
  numVal:          { fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 600, lineHeight: 1, marginBottom: 4 },
  numLabel:        { fontSize: 12, color: t.inkLight, textTransform: 'uppercase', letterSpacing: 1 },
  seccion:         { marginBottom: 28 },
  secTitulo:       { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 400, color: t.ink, marginBottom: 14 },
  secHint:         { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 14, color: t.inkLight, fontWeight: 400 },
  donutCard:       { background: t.paper, border: `1px solid ${t.blush}44`, borderRadius: 24, padding: 20, display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 },
  donutWrap:       { position: 'relative', flexShrink: 0 },
  donutCenter:     { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  donutCenterLabel:{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: t.inkLight, marginTop: 2 },
  donutSide:       { flex: 1, minWidth: 0 },
  donutEye:        { fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, fontFamily: "'DM Sans',sans-serif", margin: 0 },
  donutBig:        { fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 600, color: t.ink, marginTop: 3, marginBottom: 0 },
  donutSm:         { fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: t.inkLight, marginTop: 4, lineHeight: 1.3, marginBottom: 0 },
  legend:          { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 0 },
  legItem:         { display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 500, color: t.inkMid },
  legDot:          { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  legVal:          { marginLeft: 'auto', fontFamily: "'Cormorant Garamond',serif", fontSize: 14, color: t.ink },
  barRow:          { marginBottom: 14 },
  barTop:          { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 },
  barNombre:       { fontSize: 15, fontWeight: 500, color: t.ink },
  barMonto:        { fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontWeight: 600, color: t.terra },
  barTrack:        { height: 8, background: `${t.blush}44`, borderRadius: 8, overflow: 'hidden' },
  barFill:         { height: '100%', borderRadius: 8, background: `linear-gradient(90deg, ${t.blush}, ${t.terra})` },
  heatCard:        { background: t.paper, border: `1px solid ${t.blush}44`, borderRadius: 20, padding: 16 },
  heatGrid:        { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 10 },
  heatLabel:       { fontSize: 11, color: t.inkLight, textAlign: 'center', paddingBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  heatCell:        { aspectRatio: '1', borderRadius: 6 },
  heatLegend:      { display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' },
  heatLegendLabel: { fontSize: 11, color: t.inkLight },
  insightsList:    { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },
  insightCard:     { background: 'rgba(142,170,122,0.15)', border: '1.5px solid rgba(142,170,122,0.35)', borderRadius: 16, padding: 14, display: 'flex', gap: 10, alignItems: 'flex-start' },
  insightWarn:     { background: 'rgba(240,196,168,0.3)', borderColor: 'rgba(200,120,78,0.3)' },
  insightIc:       { width: 34, height: 34, borderRadius: 10, background: 'rgba(142,170,122,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.sage, flexShrink: 0 },
  insightIcWarn:   { background: t.accent + '33', color: t.terraDeep },
  insightH4:       { margin: 0, fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: t.ink, lineHeight: 1.2 },
  insightP:        { margin: '2px 0 0', fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 14, color: t.inkMid, lineHeight: 1.5 },
  vacio:           { textAlign: 'center', padding: '32px 0' },
  vacioTexto:      { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 16, color: t.inkLight, marginBottom: 8 },
  vacioSub:        { fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: t.inkLight },
  budgetCard:      { background: t.paper, border: `1px solid ${t.blush}44`, borderRadius: 20, padding: '16px 18px', marginBottom: 12 },
  budgetRow:       { display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 10 },
  budgetLabel:     { fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500 },
  budgetVal:       { fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 600, color: t.ink, marginLeft: 'auto' },
  budgetSep:       { fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: t.inkLight },
  budgetTotal:     { fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 500 },
  budgetTrack:     { height: 8, background: `${t.blush}44`, borderRadius: 8, overflow: 'hidden', marginBottom: 6 },
  budgetFill:      { height: '100%', borderRadius: 8, transition: 'width 0.4s' },
  budgetFooter:    { display: 'flex', justifyContent: 'space-between', fontFamily: "'DM Sans', sans-serif", fontSize: 13 },
  budgetCatRow:    { marginBottom: 10 },
  budgetCatTop:    { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 },
  budgetCatNombre: { fontSize: 13, fontWeight: 500, color: t.inkMid },
  budgetCatMonto:  { fontFamily: "'Cormorant Garamond',serif", fontSize: 15, fontWeight: 600 },
  budgetCatTrack:  { height: 5, background: `${t.blush}33`, borderRadius: 5, overflow: 'hidden' },
  budgetCatFill:   { height: '100%', borderRadius: 5, transition: 'width 0.4s' },
})
